import {useCallback, useMemo, useState} from 'react';
import {Box, Button, Checkbox, Typography} from '@mui/material';
import {isAxiosError} from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  formatSportMatchStatus,
  SportMatchActivationItem,
  useActivateSportMatches,
  useGetSportMatches,
} from '@entities/sportMatch';
import {getErrorMessageFromApiError} from '@shared/api';
import {
  centerAlignmentColumnProps,
  MrTable,
  openConfirmation,
} from '@shared/components';
import {
  FilterRow,
  SearchFilter,
  SelectFilter,
} from '@shared/components/filters';
import {
  ACTIVATION_STATUS_FILTER,
  DATETIME_FORMAT,
  entityTableCommonProps,
} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {formatInTz} from '@shared/utils';

import {PaginationState} from '@/shared/types';

const SPORT_OPTIONS = [{value: 'basketball', label: 'Basketball'}];

type UrlState = PaginationState & {
  sport: string;
  activated: boolean | null;
  search?: string;
};

export const SportMatchesPage = () => {
  const {enqueueSnackbar} = useSnackbar();
  const {urlState, setUrl} = useUrlState<UrlState>({
    pageIndex: 0,
    pageSize: 10,
    sport: 'basketball',
    activated: null,
  });
  const [selectedMatchIds, setSelectedMatchIds] = useState<Set<string>>(
    new Set(),
  );
  const {mutateAsync: activateMatches, isPending: isActivating} =
    useActivateSportMatches();
  const {data, isPending, error} = useGetSportMatches(urlState.sport, {
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
    hideFinished: true,
    activated: urlState.activated ?? undefined,
    search: urlState.search || undefined,
  });

  const activatableCurrentPageMatches = useMemo(
    () => (data?.items ?? []).filter(match => !match.activatedAt),
    [data?.items],
  );
  const areAllCurrentPageMatchesSelected =
    activatableCurrentPageMatches.length > 0 &&
    activatableCurrentPageMatches.every(match =>
      selectedMatchIds.has(match.id),
    );
  const areSomeCurrentPageMatchesSelected =
    activatableCurrentPageMatches.some(match =>
      selectedMatchIds.has(match.id),
    ) && !areAllCurrentPageMatchesSelected;

  const toggleCurrentPageSelection = useCallback(
    (isSelected: boolean) => {
      setSelectedMatchIds(previous => {
        const next = new Set(previous);

        for (const match of activatableCurrentPageMatches) {
          if (isSelected) {
            next.add(match.id);
          } else {
            next.delete(match.id);
          }
        }

        return next;
      });
    },
    [activatableCurrentPageMatches],
  );

  const columns = useMemo<MRT_ColumnDef<SportMatchActivationItem>[]>(
    () => [
      {
        id: 'activationSelection',
        header: '',
        Header: () => (
          <Checkbox
            checked={areAllCurrentPageMatchesSelected}
            indeterminate={areSomeCurrentPageMatchesSelected}
            disabled={activatableCurrentPageMatches.length === 0}
            onChange={(_, checked) => {
              toggleCurrentPageSelection(checked);
            }}
          />
        ),
        Cell: ({row}) => {
          const isActivated = Boolean(row.original.activatedAt);

          return (
            <Checkbox
              checked={selectedMatchIds.has(row.original.id)}
              disabled={isActivated}
              onChange={(_, checked) => {
                setSelectedMatchIds(previous => {
                  const next = new Set(previous);
                  if (checked) {
                    next.add(row.original.id);
                  } else {
                    next.delete(row.original.id);
                  }
                  return next;
                });
              }}
            />
          );
        },
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: false,
        ...centerAlignmentColumnProps,
      },
      {
        accessorKey: 'name',
        header: 'Match',
        size: 280,
      },
      {
        accessorKey: 'tournament.name',
        header: 'Tournament',
        size: 220,
      },
      {
        accessorKey: 'externalId',
        header: 'External ID',
        size: 140,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({row}) => formatSportMatchStatus(row.original.status),
        size: 100,
      },
      {
        accessorKey: 'startTime',
        header: 'Start time',
        Cell: ({row}) =>
          row.original.startTime
            ? formatInTz(row.original.startTime, DATETIME_FORMAT)
            : '-',
        size: 180,
      },
      {
        accessorKey: 'activatedAt',
        header: 'Activated at',
        Cell: ({row}) =>
          row.original.activatedAt
            ? formatInTz(row.original.activatedAt, DATETIME_FORMAT)
            : '-',
        size: 180,
      },
    ],
    [
      activatableCurrentPageMatches.length,
      areAllCurrentPageMatchesSelected,
      areSomeCurrentPageMatchesSelected,
      selectedMatchIds,
      toggleCurrentPageSelection,
    ],
  );

  const handleActivate = async () => {
    const matchIds = [...selectedMatchIds];
    if (matchIds.length === 0) return;

    try {
      await openConfirmation({
        title: `Activate ${matchIds.length} match(es)?`,
        content: (
          <Typography variant="h3" mt={1}>
            The selected matches will be sent to the activation queue.
          </Typography>
        ),
        okButtonText: 'Activate',
      });
    } catch {
      return;
    }

    try {
      const activated = await activateMatches({
        categorySlug: urlState.sport,
        matchIds,
      });
      setSelectedMatchIds(new Set());
      enqueueSnackbar(`${activated.length} match(es) activated`, {
        variant: 'success',
      });
    } catch (err) {
      const errorMessage = isAxiosError(err)
        ? getErrorMessageFromApiError(err)
        : '';
      enqueueSnackbar(`Could not activate matches. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  return (
    <MrTable
      columns={columns}
      data={data?.items ?? []}
      error={error}
      renderTopToolbarCustomActions={() => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          gap={2}
        >
          <Button
            disabled={selectedMatchIds.size === 0 || isActivating}
            onClick={() => {
              void handleActivate();
            }}
          >
            Activate selected
          </Button>

          <FilterRow>
            <SelectFilter
              value={urlState.sport}
              options={SPORT_OPTIONS}
              onChange={sport => {
                if (!sport) return;
                setSelectedMatchIds(new Set());
                setUrl(previous => ({...previous, pageIndex: 0, sport}));
              }}
            />
            <SelectFilter
              value={urlState.activated}
              options={ACTIVATION_STATUS_FILTER.options}
              onChange={activated =>
                setUrl(previous => ({
                  ...previous,
                  pageIndex: 0,
                  activated,
                }))
              }
            />
            <SearchFilter
              value={urlState.search}
              onChange={search =>
                setUrl(previous => ({
                  ...previous,
                  pageIndex: 0,
                  search: search || undefined,
                }))
              }
            />
          </FilterRow>
        </Box>
      )}
      enableColumnOrdering
      enableColumnDragging={false}
      localStorageKeyForSettings="sportMatchesTable"
      enableHiding
      enablePagination
      manualPagination
      onPaginationChange={updater =>
        setUrl(previous => {
          const pagination =
            typeof updater === 'function' ? updater(previous) : updater;
          return {...previous, ...pagination};
        })
      }
      rowCount={data?.total ?? 0}
      state={{
        isLoading: isPending || isActivating,
        pagination: urlState,
      }}
      {...entityTableCommonProps}
      enableRowActions={false}
    />
  );
};
